import * as React from "react"
import { Button, Form } from "react-bootstrap"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import styles from "../teacher.module.scss"
import { AssignmentResponse, ClassResponse } from "../../../../shared/types/teacherTypes"
import DeleteAssignment from "../Forms/DeleteAssignment"
import ToolTip from "../../../General/ToolTip"
import { useModel } from '@stem-sims/nexus'
import Assignments from "components/Dashboards/General/Assignments"
import EmptyTablePreview from "../../General/EmptyTablePreview"
import RelevantHelpModal from "components/Modals/RelevantHelpModal"
import { helpLinkVideos, helpSteps } from "./assignmentsHelpData"

import BulkAssignModal from "../Forms/AssignmentForm/BulkComponents/BulkAssignModal"
import BulkCopyModal from "../Forms/AssignmentForm/BulkComponents/BulkCopyModal"
import { AssignmentPreviewStateProps } from "components/Lessons/Grading/AssignmentPreview/Preview"
import AddReviewModal from "../Forms/AssignmentForm/AssignReviewModal"
import { teacherReviewAssignments } from "shared/routes/science35"
import { AddReviewButton } from "./ExtraComponents/AddReviewButton"
import SelectGradeLevelDropdown from "components/Dashboards/General/Science35/SelectGradeLevelDropdown"
import { ReviewGradeType } from "shared/types/science35Types"
import { TeacherCoreIdeasViewButton } from "./ExtraComponents/TeacherCoreIdeasViewButton"
import { CORE_IDEA_VIEW_OPEN_KEY } from "constants/generalConstants"
import ViewCoreIdeasBoardModal from "../Science35/ViewCoreIdeasBoardModal"
import { toBlockType } from "helpers/blockStringConverter"
import { AuthContext } from "AuthContext"
import { teacherScienceGradeLevels } from "helpers/scienceGradeLevels"

interface Props {
    activeClass: ClassResponse
}

function NoAssignmentsView({onClickAddReview}: {onClickAddReview: () => void}) {
    return (
        <>
            <EmptyTablePreview
                title="Create Assignments"
                description="Once you add an assignment, the entire class or selected students will be able to see and submit it."
            >
                <Button 
                    className="ms-2"
                    onClick={onClickAddReview}
                    variant="theme">
                    <i className="fas fa-plus pe-2" />
                    <span className="d-none d-md-inline">Assign Core Ideas</span>
                </Button>
            </EmptyTablePreview>
        </>
    )
}

export default function TeacherReviewAssignments({ activeClass }: Props) {
    const { block } = useParams() as { block: string }
    const { scienceBlocks } = React.useContext(AuthContext)

    const blockType = toBlockType(block)

    const [deleteAssignmentShown, setDeleteAssignmentShown] = React.useState(false)
    const [assignmentToBeDeleted, setAssignmentToBeDeleted] = React.useState<AssignmentResponse | null>(null)

    const [bulkDelete, setBulkDelete] = React.useState<boolean>(false)
    const [selectedAssignments, setSelectedAssignments] = React.useState<string[]>([])
    
    const [showModal, setShowModal] = React.useState<"add-review" | "view-board" | "bulk-assign" | "bulk-copy" | "delete-modal">(null)

    const [selectedGrades, setSelectedGrades] = React.useState<ReviewGradeType[]>([])

    const filterGrades = teacherScienceGradeLevels(blockType, scienceBlocks)

    const navigate = useNavigate()

    function assignmentError(err) {
        toast.error(err.response?.data?.message ?? "There has been an error loading the assignments. Please try again.")
    }

    const { response: assignments, loading, refreshModel: reloadAssignments } = useModel({
        model: teacherReviewAssignments,
        onError: assignmentError,
        props: { classID: activeClass.id, gradeLevels: selectedGrades, targetBlock: blockType },
    })

    React.useEffect(() => {
        setSelectedGrades([]) // Reset grade filter when switching between blocks
    }, [blockType])

    React.useEffect(() => {
        reloadAssignments()
        setSelectedAssignments([])
    },
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [activeClass])

    React.useEffect(() => {
        const showBoardView = localStorage.getItem(CORE_IDEA_VIEW_OPEN_KEY) === "true"
        if (showBoardView) {
            setShowModal("view-board")
            localStorage.removeItem(CORE_IDEA_VIEW_OPEN_KEY)
        }
    }, [])

    const extraCols = [
        {
            id: 'Actions',
            disableSortBy: true,
            Cell: ({ row }) => (
                <div className="d-flex justify-content-center">
                    <ToolTip title="View Assignment">
                        <Button
                            className="me-2"
                            variant="outline-primary"
                            aria-label="View Assignment"
                            disabled={!row.original.userHasAccess}
                            onClick={(e) => {
                                e.stopPropagation()
                                navigate(`/dashboard/teacher/science/${block}/assignments/preview`, {
                                    state: {
                                        assignment: row.original,
                                        lesson: row.original.lesson,
                                        origin: "assignments",
                                        blockType: blockType
                                    } as AssignmentPreviewStateProps
                                })
                            }}
                        >
                            <i className="fas fa-glasses" />
                        </Button>
                    </ToolTip>
                    <ToolTip title="Edit Assignment">
                        <Button
                            data-testid="edit-assignment-button"
                            className="me-2 class-assignment-edit"
                            variant="outline-primary"
                            aria-label="Edit Assignment"
                            disabled={!row.original.userHasAccess}
                            onClick={(e) => {
                                e.stopPropagation()
                                navigate(`/dashboard/teacher/science/${block}/assignments/edit`, {
                                    state: {
                                        assignmentId: row.original.id,
                                        blockType: blockType,
                                    }
                                })
                            }}
                        >
                            <i className="fas fa-edit" />
                        </Button>
                    </ToolTip>
                    <ToolTip title="Copy Assignment">
                        <Button
                            data-testid="copy-assignment-button"
                            className="me-2"
                            variant="outline-primary"
                            aria-label="Copy Assignment"
                            onClick={(e) => {
                                e.stopPropagation()
                                setSelectedAssignments([row.original.id])
                                setShowModal("bulk-copy")
                            }}
                        >
                            <i className="fas fa-copy" />
                        </Button>
                    </ToolTip>
                    <ToolTip title="Delete Assignment">
                        <Button
                            onClick={(e) => {
                                e.stopPropagation()
                                setAssignmentToBeDeleted(row.original)
                                setBulkDelete(false)
                                setDeleteAssignmentShown(true)
                            }}
                            data-testid="delete-assignment-button"
                            variant="outline-danger"
                            aria-label="Delete Assignment"
                        >
                            <i className="fas fa-trash-alt" />
                        </Button>
                    </ToolTip>
                </div>
            ),
        }
    ]

    const SelectAllCheckbox = (table: { page: Array<{ id: string }> }) => {
        return <Form.Check
            id="select-all-checkbox"
            type="checkbox"
            className={`${styles.checkAll}`}
            aria-label="select-all-checkbox"
            onClick={(e) => e.stopPropagation()}
            checked={table.page.length > 0 && table.page.length === selectedAssignments.length}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                if (e.target.checked) {
                    setSelectedAssignments(table.page.map(row => row.id))
                } else {
                    setSelectedAssignments([])
                }
            }}
        />
    }

    const checkboxCol = {
        Header: SelectAllCheckbox,
        disableSortBy: true,
        maxWidth: 15,
        accessor: 'select',
        Cell: ({ row }) => {
            return (
                <Form.Check
                    key={`${row.original.id}`}
                    id={`${row.original.id}`}
                    type="checkbox"
                    className={styles.assignmentCheckbox}
                    aria-label={`${row.original.title}`}
                    onClick={(e) => e.stopPropagation()}
                    checked={selectedAssignments.includes(row.original.id)}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        if (e.target.checked) {
                            setSelectedAssignments(prev => [...prev, row.original.id])
                        } else {
                            setSelectedAssignments(prev => [...prev.filter(id => id !== row.original.id)])
                        }
                    }}
                />
            )
        }
    }

    const onRowClick = async (row) => {
        navigate(`/dashboard/teacher/science/${block}/assignments/preview`, {
            state: {
                assignment: row.original as AssignmentResponse,
                origin: "assignments",
                blockType: blockType,
            } as AssignmentPreviewStateProps
        })
    }

    const changeBackgroundColor = (row) => {
        const assignment: AssignmentResponse = row.original
        return assignment.userHasAccess ? "" : `${styles.unavailableAssignment}`
    }

    const onClickAddReviewAssignments = () => {
        setShowModal("add-review")
    }

    const onClickViewCoreIdeas = () => {
        setShowModal("view-board")
    }

    const extraComponents = [{component: AddReviewButton, prop: onClickAddReviewAssignments}, { component: TeacherCoreIdeasViewButton, prop: onClickViewCoreIdeas }]

    const AssignmentsComponent = <Assignments origin="sci35" assignments={assignments} extraTeacherComponents={extraComponents} onRowClick={onRowClick} changeBackgroundColor={changeBackgroundColor} extraCols={extraCols} checkboxCol={checkboxCol} loading={loading} rowTooltip="View Assignment" />
    const EmptyTableComponent = <NoAssignmentsView onClickAddReview={onClickAddReviewAssignments} />

    const validFilterGrades = filterGrades.filter(grade => grade.isValid)

    return (
        <>
            <RelevantHelpModal videos={helpLinkVideos} helpSteps={assignments?.length > 0 && helpSteps}/>
            <h1 className="h2 text-start mt-3">Class Assignments</h1>
            <p className={`text-start ${styles.greyText}`}>
                Use the corresponding buttons to view, edit, copy, or delete an assignment. Check boxes to select multiple assignments.
            </p>
            {validFilterGrades.length > 0 && (
                <div className="ms-2">
                    <SelectGradeLevelDropdown grades={filterGrades} selectedGrades={selectedGrades} onSelect={(grades) => setSelectedGrades(grades)} />
                </div>
            )}
            <div className={`d-flex align-items-center position-relative`}>
                <div className={`p-0 ${styles.bulkItemsContainer} ${selectedAssignments.length > 0 ? styles.displayActions : styles.hiddenActions}`}>
                    <div className={`d-flex align-items-center p-0 mb-0 position-relative`}>
                        <ToolTip title="Delete Selected Assignments" classNames={selectedAssignments.length > 0 ? "d-block" : "d-none"}>
                            <Button
                                variant=""
                                className="border-end rounded-0"
                                disabled={selectedAssignments.length === 0}
                                onClick={() => {
                                    setBulkDelete(true)
                                    setDeleteAssignmentShown(true)
                                }} >
                                <i className="fas fa-trash-alt pe-2 text-red" />
                                <span className="d-none d-md-inline">Delete</span>
                            </Button>
                        </ToolTip>
                        <ToolTip title="Assign To Class or Students" classNames={selectedAssignments.length > 0 ? "d-block" : "d-none"}>
                            <Button
                                variant=""
                                className="border-end rounded-0"
                                disabled={selectedAssignments.length === 0}
                                onClick={() => {
                                    setShowModal("bulk-assign")
                                }} >
                                <i className="fas fa-plus pe-2" />
                                <span className="d-none d-md-inline">Assign</span>
                            </Button>
                        </ToolTip>
                        <ToolTip title="Copy To Another Class" classNames={selectedAssignments.length > 0 ? "d-block" : "d-none"}>
                            <Button
                                variant=""
                                disabled={selectedAssignments.length === 0}
                                onClick={() => {
                                    setShowModal("bulk-copy")
                                }} >
                                <i className="fas fa-copy pe-2" />
                                <span className="d-none d-md-inline">Copy</span>
                            </Button>
                        </ToolTip>
                        <span className={`bg-primary text-white rounded-pill ${styles.selectedCount}`}>{selectedAssignments.length}</span>
                    </div>
                </div>
            </div>

            {(loading || assignments?.length > 0) ? AssignmentsComponent : EmptyTableComponent}
            <DeleteAssignment
                bulkDelete={bulkDelete}
                selectedAssignmentIds={selectedAssignments}
                assignment={assignmentToBeDeleted}
                show={deleteAssignmentShown}
                onHide={() => setDeleteAssignmentShown(false)}
                onSuccess={() => {
                    reloadAssignments()
                    setSelectedAssignments([])
                }}
            />
            <BulkAssignModal
                activeClass={activeClass}
                show={showModal === "bulk-assign"}
                onHide={() => setShowModal(null)}
                onSuccess={() => {
                    setShowModal(null)
                    setSelectedAssignments([])
                    reloadAssignments()
                }}
                selectedAssignmentIds={selectedAssignments}
            />
            <BulkCopyModal
                activeClass={activeClass}
                show={showModal === "bulk-copy"}
                onHide={() => setShowModal(null)}
                hidePlanSelector
                onSuccess={() => {
                    setShowModal(null)
                    setSelectedAssignments([])
                }}
                selectedAssignmentIds={selectedAssignments}
            />
            <AddReviewModal
                activeClass={activeClass}
                filterGrades={filterGrades}
                show={showModal === "add-review"}
                blockType={blockType}
                onHide={() => {
                    reloadAssignments()
                    setShowModal(null)
                }}
                selectedExistingGrades={selectedGrades}
            />

            <ViewCoreIdeasBoardModal
                show={showModal === "view-board"}
                hide={() => setShowModal(null)}
                assignments={assignments}
                blockType={blockType}
             />
        </>
    )
}
